import { Plan, PlanExtra, PlanExtraDetail } from '@dtx-company/true-common/src/types/Plan'
import { Subscription } from '../../types/subscription'
import { SubscriptionServiceGetSubscriptionsJSON } from './types/jsons/SubscriptionServiceGetSubscriptionsJSON'
import { SubscriptionServiceSubscription } from './types/models/SubscriptionServiceSubscription'
import { SubscriptionStatus } from '../../types/subService'
import { fetchSubscriptionServiceUrl } from './fetchSubscriptionServiceUrl'
import { getPlans } from './getPlans'
import { subscriptionServiceDateStringToDate } from '../../utils/misc/subscriptionServiceDateStringToDate'
import compact from 'lodash/compact'
import uniq from 'lodash/uniq'

const GET_SUBSCRIPTIONS_URL = '/v2/subscriptions'

export interface GetSubscriptionsOptionsFilter {
  isCurrent?: boolean
}

export interface GetSubscriptionsOptions {
  filter?: GetSubscriptionsOptionsFilter
  token?: string
}

const subscriptionStatuses: string[] = Object.values(SubscriptionStatus)

const toSubscriptionStatus = (jsonStatus: string): SubscriptionStatus => {
  if (!subscriptionStatuses.includes(jsonStatus)) {
    console.error(`Unable to convert ${jsonStatus} to a SubscriptionStatus`)
  }
  return jsonStatus as SubscriptionStatus
}

function toPlanExtraDetails(
  jsonPlanExtraDetails: PlanExtraDetail[],
  planExtras: PlanExtra[]
): PlanExtraDetail[] {
  return (jsonPlanExtraDetails || []).map(jsonPlanExtraDetail => {
    return {
      planExtraId: jsonPlanExtraDetail.planExtraId,
      quantity: jsonPlanExtraDetail.quantity,
      planExtra: planExtras.find(
        planExtra => planExtra.planExtraId === jsonPlanExtraDetail.planExtraId
      )
    }
  })
}

function migrateSubscriptions(
  jsonSubscriptions: SubscriptionServiceSubscription[],
  plans: Plan[]
): Subscription[] {
  const planExtras = plans.reduce<PlanExtra[]>((allPlanExtras, plan) => {
    return [...allPlanExtras, ...(plan.planExtras || [])]
  }, [])
  return jsonSubscriptions.map(jsonSubscription => {
    const plan = plans.find(plan => plan.id === jsonSubscription.planId)
    if (!plan) {
      throw Error(`Plan with ID ${jsonSubscription.planId} doesn't exist`)
    } else {
      return {
        id: jsonSubscription.subscriptionId,
        planId: jsonSubscription.planId,
        plan,
        status: toSubscriptionStatus(jsonSubscription.subscriptionStatus),
        planExtraDetails: toPlanExtraDetails(jsonSubscription.planExtraDetails, planExtras),
        eligibleForFreeMonth: jsonSubscription.eligibleForFreeMonth,
        queuedDowngradePlanId: jsonSubscription.queuedDowngradePlanId || undefined,
        paymentMethod: jsonSubscription.paymentMethod
          ? {
              id: jsonSubscription.paymentMethod.id,
              type: jsonSubscription.paymentMethod.methodType,
              creditCard: jsonSubscription.paymentMethod.cardInfo
            }
          : undefined,
        nextPaymentDate: subscriptionServiceDateStringToDate(jsonSubscription.billingCycleEnd, {
          apiName: GET_SUBSCRIPTIONS_URL
        }),
        queuedPlan: plans.find(plan => plan.id === jsonSubscription.queuedDowngradePlanId),
        trialEndAt: subscriptionServiceDateStringToDate(jsonSubscription.trialEndAt, {
          apiName: GET_SUBSCRIPTIONS_URL
        })
      }
    }
  })
}

/**
 * Calls the subscription service GET /v2/plans endpoint.
 *
 * May be used as a fetcher for useSWR or by itself for SSR.
 *
 * @docs https://backstage.use1.dev.services.flowcode.systems/catalog/default/api/subscription-service-rest-api/definition#/Plans/get_v2_plans
 */
export async function getSubscriptions(options?: GetSubscriptionsOptions): Promise<Subscription[]> {
  const response = await fetchSubscriptionServiceUrl<SubscriptionServiceGetSubscriptionsJSON>({
    token: options?.token,
    url: GET_SUBSCRIPTIONS_URL,
    query: {
      'filter[isCurrent]': options?.filter?.isCurrent
    }
  })
  if ([404, 401, 403].includes(response.status)) {
    return []
  }
  if (!response.ok) {
    throw new Error(
      `Received http status ${response.status} from ${GET_SUBSCRIPTIONS_URL} endpoint`
    )
  }
  if (!response.json) {
    throw new Error(`The ${GET_SUBSCRIPTIONS_URL} endpoint did not return a json payload`)
  }

  const jsonSubscriptions = response.json.subscriptions || []

  // Load all associated plans
  const planIds = uniq(
    compact([
      ...jsonSubscriptions.map(jsonSubscription => jsonSubscription.planId),
      ...jsonSubscriptions.map(jsonSubscription => jsonSubscription.queuedDowngradePlanId)
    ])
  )
  const plans = planIds.length > 0 ? await getPlans({ filter: { planId: planIds } }) : []

  const subscriptions = migrateSubscriptions(jsonSubscriptions, plans)
  return subscriptions
}
